import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { useUiStore } from "@/store/ui";
import SpeechStreamAnimation from "@/components/SpeechStreamAnimation";

// Mobile detection hook for high-performance mobile browsing
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

// Editorial Animated Profile Graphics

function AnimatedRetailerGraphic({ isDark = false }: { isDark?: boolean }) {
  const isMobile = useIsMobile();
  return (
    <div
      className={`relative w-16 h-16 rounded-2xl ${
        isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
      } flex items-center justify-center overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.04)] transition-all duration-300 shrink-0`}
    >
      {/* Pastel Atmospheric Bloom */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#a7e5d3]/20 to-[#f4c5a8]/20 opacity-70" />

      {/* Scanning Beam */}
      <motion.div
        animate={isMobile ? undefined : { y: ["-100%", "220%"] }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 2.4, ease: "linear" }}
        className={`absolute inset-x-0 h-1 bg-gradient-to-r from-transparent ${
          isDark ? "via-white/40" : "via-[#292524]/40"
        } to-transparent`}
      />

      {/* Floating 3D Crate */}
      <motion.div
        animate={isMobile ? undefined : { y: [-2, 2, -2] }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
        className="relative z-10 flex flex-col items-center justify-center"
      >
        <svg
          className={`w-8 h-8 ${isDark ? "text-white" : "text-[#0c0a09]"}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </svg>

        {/* Sparkle Nodes */}
        <motion.div
          animate={isMobile ? undefined : { scale: [0.8, 1.2, 0.8], opacity: [0.5, 1, 0.5] }}
          transition={isMobile ? undefined : { repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#a7e5d3] border border-[#292524]/20"
        />
        <motion.div
          animate={isMobile ? undefined : { scale: [1.2, 0.8, 1.2], opacity: [0.4, 0.9, 0.4] }}
          transition={isMobile ? undefined : { repeat: Infinity, duration: 2.5, ease: "easeInOut", delay: 0.5 }}
          className="absolute -bottom-1 -left-1 w-2 h-2 rounded-full bg-[#f4c5a8] border border-[#292524]/20"
        />
      </motion.div>
    </div>
  );
}

function AnimatedCreatorGraphic({ isDark = false }: { isDark?: boolean }) {
  const isMobile = useIsMobile();
  return (
    <div
      className={`relative w-16 h-16 rounded-2xl ${
        isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
      } flex items-center justify-center overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.04)] transition-all duration-300 shrink-0`}
    >
      {/* Pastel Atmospheric Bloom */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#c8b8e0]/20 to-[#a8c8e8]/20 opacity-70" />

      {/* Radar Wave Rings */}
      <motion.div
        animate={isMobile ? undefined : { scale: [0.6, 1.7], opacity: [0.6, 0] }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 2.2, ease: "easeOut" }}
        className="absolute w-10 h-10 rounded-full border border-[#c8b8e0] pointer-events-none"
      />
      <motion.div
        animate={isMobile ? undefined : { scale: [0.6, 1.7], opacity: [0.6, 0] }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 2.2, ease: "easeOut", delay: 0.9 }}
        className="absolute w-10 h-10 rounded-full border border-[#a8c8e8] pointer-events-none"
      />

      {/* Video Camera */}
      <motion.div
        animate={isMobile ? undefined : { scale: [1, 1.05, 1] }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 2.6, ease: "easeInOut" }}
        className="relative z-10 flex items-center justify-center"
      >
        <svg
          className={`w-8 h-8 ${isDark ? "text-white" : "text-[#0c0a09]"}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M23 7l-7 5 7 5V7z" />
          <rect x="1" y="5" width="15" height="14" rx="3" ry="3" />
        </svg>

        {/* Live Indicator */}
        <motion.div
          animate={isMobile ? undefined : { scale: [1, 1.3, 1], opacity: [1, 0.4, 1] }}
          transition={isMobile ? undefined : { repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          className="absolute w-1.5 h-1.5 rounded-full bg-[#dc2626]"
          style={{ left: "5px", top: "7px" }}
        />
      </motion.div>
    </div>
  );
}

function AnimatedMerchantGraphic({ isDark = false }: { isDark?: boolean }) {
  const isMobile = useIsMobile();
  return (
    <div
      className={`relative w-16 h-16 rounded-2xl ${
        isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
      } flex items-center justify-center overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.04)] transition-all duration-300 shrink-0`}
    >
      {/* Pastel Atmospheric Bloom */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#e8b8c4]/20 to-[#f4c5a8]/20 opacity-70" />

      {/* Rotating Dialect Reticle */}
      <motion.div
        animate={isMobile ? undefined : { rotate: 360 }}
        transition={isMobile ? undefined : { repeat: Infinity, duration: 16, ease: "linear" }}
        className={`absolute w-12 h-12 rounded-full border border-dashed ${
          isDark ? "border-white/20" : "border-[#292524]/20"
        } pointer-events-none`}
      />

      {/* Studio Mic & Equalizer */}
      <div className="relative z-10 flex flex-col items-center justify-center">
        <svg
          className={`w-7 h-7 ${isDark ? "text-white" : "text-[#0c0a09]"}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
          <line x1="8" y1="23" x2="16" y2="23" />
        </svg>

        {/* Equalizer Bars */}
        <div className="flex items-center gap-0.5 mt-1 h-3.5">
          {[4, 9, 13, 7, 11, 5].map((h, i) => (
            <div
              key={i}
              className={`w-0.5 rounded-full wave-bar ${isDark ? "bg-white" : "bg-[#292524]"}`}
              style={{
                height: `${h}px`,
                animationDelay: `${i * 0.12}s`,
                animationDuration: `${0.8 + i * 0.1}s`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

const TRANSLATIONS = {
    en: {
      title: "Flawless Iraqi Arabic. Using Your Own Voice.",
      subtitle:
        "Clones your exact voice into fluent Iraqi Arabic so Arab buyers trust your store instantly—without you speaking a single word.",
      ctaPrimary: "Upload Your First Video",
      ctaSecondary: "Explore Interactive Demo",
      statsAccuracy: "100%",
      statsAccuracyLabel: "Baghdad Dialect Authenticity",
      statsSpeed: "1/3x",
      statsSpeedLabel: "Real-Time Processing Speed",
      statsQuality: "4K UHD",
      statsQualityLabel: "Lossless Lip-Sync Video Export",
      statsStems: "Dual-Stem",
      statsStemsLabel: "Original BGM & Audio Isolation",
      demoTitle: "Hear the Difference in Real-Time",
      originalBadge: "Original Audio (Kurdish Sorani)",
      dubbedBadge: "AI Dubbed (Iraqi Arabic - Baghdad)",
      speaker1Name: "Ahmed K.",
      kurdishTranscript: "ئەم مۆبایلە نوێیە یەکێکە لە باشترین بەرهەمەکانی ئەمساڵ، کامێراکەی زۆر بەهێزە و پاترییەکەی بە درێژایی ڕۆژ دەمێنێتەوە.",
      iraqiTranscript: "هذا التلفون الجديد واحد من أحسن الأجهزة هالسنة، كاميرته كلش قوية وبطاريته تكفيك طول اليوم بدون أي نقص.",
      featuresHeading: "Features",
      howItWorksHeading: "How it Works",
      pricingHeading: "Pricing",
      faqHeading: "FAQ",
      howItWorksTitle: "From Kurdish Video to Arab Customers in 3 steps",
      howItWorksSubtitle: "",
      step1Title: "1. Drop Your Kurdish Video",
      step1Desc: "No complex settings or editing needed. Simply send your normal Sorani shop video from your phone—our system handles the rest automatically.",
      step2Title: "2. Automatic Voice Clone",
      step2Desc: "The AI keeps your exact voice, tone, and store background audio, translating your speech into natural Iraqi Arabic so tourists trust your store instantly.",
      step3Title: "3. Post & Capture Tourists",
      step3Desc: "Download your ready-to-post video in seconds, upload it directly to TikTok or Instagram, and start reaching Arab buyers walking past your door.",
      useCasesTitle: "Who Doblaj AI Is Built For",
      useCasesSubtitle: "",
      useCase1Title: "The Inventory-Trapped Retailer",
      useCase1Desc: "Built for store owners who have capital tied up in unsold stock and need immediate cash flow from high-spending Arab tourists walking past their door.",
      useCase1Tags: ["Immediate Cash Flow", "Tourist Foot-Traffic"],
      useCase2Title: "The Invisible Video Creator",
      useCase2Desc: "Built for merchants who put effort into social media but remain invisible to Arab feeds because algorithms ignore subtitled videos.",
      useCase2Tags: ["Native Feed Reach", "Algorithm Boost"],
      useCase3Title: "The Authenticity-Focused Merchant",
      useCase3Desc: "Built for established business owners who want to clone their own voice into fluent Iraqi Arabic so Arab buyers trust them instantly.",
      useCase3Tags: ["Exact Voice Timbre", "Instant Dialect Trust"],
      faqTitle: "Frequently Asked Questions",
      faqs: [
        {
          q: "How fast is the dubbing turnaround time?",
          a: "Our GPU pipeline processes video at roughly 1/3 of the real-time runtime. A standard 3-minute video is transcribed, translated to Iraqi dialect, synthesized, and rendered in about 60 seconds.",
        },
        {
          q: "Does Doblaj preserve the speaker's original voice timbre and emotion?",
          a: "Yes! Our zero-shot voice cloning engine captures the emotional intonation, pitch curve, and unique character of the original speaker, speaking natural Baghdad Arabic.",
        },
        {
          q: "What happens to the background music and sound effects in my video?",
          a: "Our acoustic stem separator automatically splits the voice track from ambient sound and music. We replace only the spoken dialogue and remux the original background track losslessly.",
        },
        {
          q: "Does it support videos with multiple speakers?",
          a: "Yes. The AI pipeline performs automatic speaker diarization, identifying distinct voices and applying unique cloned voices to each speaker in the video.",
        },
        {
          q: "What formats and resolutions are supported?",
          a: "We support MP4, MOV, MKV, and WEBM video uploads up to 4K resolution (3840x2160) at 60fps.",
        },
      ],
      pricingTitle: "Transparent & Flexible Pricing",
      pricingSubtitle: "No hidden fees. Choose the production volume that fits your studio needs.",
      starterTitle: "Starter",
      starterPrice: "$10",
      starterPeriod: "/mo",
      starterRate: "5 Minutes ($2.00/min)",
      starterF1: "Standard GPU queue speed",
      starterF2: "1080p Full HD video export",
      starterF3: "Single & dual speaker support",
      starterF4: "Standard voice cloning model",
      starterCta: "Get 5 Minutes",
      proTitle: "Pro",
      proPrice: "$20",
      proPeriod: "/mo",
      proRate: "15 Minutes ($1.33/min)",
      proBadge: "Most Popular",
      proF1: "Priority GPU worker queue",
      proF2: "4K UHD video export",
      proF3: "Premium emotional Baghdad voices",
      proF4: "Up to 5 speakers diarization",
      proF5: "Lossless background audio remix",
      proCta: "Get 15 Minutes",
      creatorTitle: "Creator",
      creatorPrice: "$99",
      creatorPeriod: "/mo",
      creatorRate: "120 Minutes ($0.82/min)",
      creatorF1: "Highest priority instant queue",
      creatorF2: "4K 60fps lossless master export",
      creatorF3: "Unlimited multi-speaker diarization",
      creatorF4: "Custom studio voice fine-tuning",
      creatorF5: "Direct API Access & Webhooks",
      creatorCta: "Get 120 Minutes",
      footerDesc: "Synthesizing cross-dialect communication. Precision AI video dubbing bridging Kurdish Sorani and Iraqi Arabic media.",
      footerAddress1: "FIXDAI LLC (d/b/a Doblaj)",
      footerAddress2: "3801 N Capital of Texas Hwy, Ste E240 #3958",
      footerAddress3: "Austin, TX 78746, Travis County, Texas, USA",
      footerRights: "© 2026 FIXDAI LLC d/b/a Doblaj. All rights reserved.",
      startTrial: "Start",
      dashboard: "Dashboard",
    },
    ckb: {
      title: "عەرەبی عێراقیی پاراو، بە دەنگی خۆت.",
      subtitle:
        "دەنگت بە وردی کۆپی دەکات بۆ عەرەبی عێراقی پاراو تاکو کڕیارە عەرەبەکان دەستبەجێ متمانە بە فرۆشگاکەت بکەن—بێ ئەوەی یەك وشەش بڵێیت.",
      ctaPrimary: "یەکەم ڤیدیۆت باربکە",
      ctaSecondary: "سەیری دیمۆی ڕاستەوخۆ بکە",
      statsAccuracy: "١٠٠٪",
      statsAccuracyLabel: "شێوەزاری ڕەسەنی بەغدادی",
      statsSpeed: "١/٣ کات",
      statsSpeedLabel: "خێرایی پرۆسێسکردنی خێرا",
      statsQuality: "4K UHD",
      statsQualityLabel: "کواڵێتی بەرزی ڤیدیۆ بە سینکەوە",
      statsStems: "دوو ڕێڕەو",
      statsStemsLabel: "پاراستنی تەواوی میوزیك و دەنگی باکگراوند",
      demoTitle: "جیاوازییەکە بە شێوەی ڕاستەوخۆ ببیستە",
      originalBadge: "دەنگی ڕەسەن (کوردی سۆرانی)",
      dubbedBadge: "دۆبلاژکراو (عەرەبی عێراقی - بەغدا)",
      speaker1Name: "ئەحمەد ک.",
      kurdishTranscript: "ئەم مۆبایلە نوێیە یەکێکە لە باشترین بەرهەمەکانی ئەمساڵ، کامێراکەی زۆر بەهێزە و پاترییەکەی بە درێژایی ڕۆژ دەمێنێتەوە.",
      iraqiTranscript: "هذا التلفون الجديد واحد من أحسن الأجهزة هالسنة، كاميرته كلش قوية وبطاريته تكفيك طول اليوم بدون أي نقص.",
      featuresHeading: "تایبەتمەندییەکان",
      howItWorksHeading: "چۆن کاردەکات",
      pricingHeading: "نرخەکان",
      faqHeading: "پرسیارە باوەکان",
      howItWorksTitle: "لە ڤیدیۆی کوردییەوە بۆ کڕیاری عەرەب بە ٣ هەنگاو",
      howItWorksSubtitle: "",
      step1Title: "١. ڤیدیۆ کوردییەکەت دابنێ",
      step1Desc: "هیچ ڕێکخستن یان دەستکارییەکی ئاڵۆز پێویست ناکات. تەنها ڤیدیۆی ئاسایی فرۆشگاکەت بە سۆرانی لە مۆبایلەکەتەوە بنێرە—سیستەمەکەمان هەموو شتێک بە شێوەیەکی ئۆتۆماتیکی ئەنجام دەدات.",
      step2Title: "٢. کۆپیکردنی ئۆتۆماتیکی دەنگت",
      step2Desc: "زیرەکی دەستکرد هەمان دەنگ، تۆن و دەنگی باکگراوندی فرۆشگاکەت دەپارێزێت، قسەکانت دەکاتە عەرەبی عێراقی پاراو تا گەشتیاران دەستبەجێ متمانە بە فرۆشگاکەت بکەن.",
      step3Title: "٣. بڵاوکردنەوە و ڕاکێشانی گەشتیاران",
      step3Desc: "ڤیدیۆ ئامادەکراوەکەت لە چەند چرکەیەکدا دابگرە، ڕاستەوخۆ لە تیکتۆک یان ئینستاگرام دایبنێ و کڕیارە عەرەبەکان ڕابکێشە کە بە بەردەم دەرگاکەتدا تێدەپەڕن.",
      useCasesTitle: "دۆبلاژ بۆ کێ دروستکراوە؟",
      useCasesSubtitle: "",
      useCase1Title: "خاوەن کارە گیرخواردووەکان بە کەلوپەلی نەفرۆشراو",
      useCase1Desc: "بۆ ئەو خاوەن فرۆشگایانەی سەرمایەکەیان لە کەلوپەلی کۆگاکاندا قەتیس ماوە و پێویستیان بە فرۆش و پارەی کاشی خێرایە لەو گەشتیارە عەرەبانەی ڕۆژانە بە بەردەم فرۆشگاکەیاندا تێدەپەڕن.",
      useCase1Tags: ["پارەی کاشی خێرا", "ڕاکێشانی گەشتیاران"],
      useCase2Title: "دروستکەرانی ڤیدیۆی بێ ئەنجام",
      useCase2Desc: "بۆ ئەو کاسپکارانەی کات و ماندووبوون لە تۆڕە کۆمەڵایەتییەکاندا سەرف دەکەن بەڵام بۆ بەکارهێنەرانی عەرەب دیار نین چونكە ئەلگۆریتمەکان ڤیدیۆی ژێرنووسکراو پشتگوێ دەخەن.",
      useCase2Tags: ["گەیشتن بە بینەری عەرەب", "زیادکردنی بینەر"],
      useCase3Title: "کاسپکارانی خاوەن متمانە و ناوبانگ",
      useCase3Desc: "بۆ خاوەن کارە ناسراوەکان کە دەیانەوێت دەنگی خۆیان بکەنە عەرەبی عێراقی پاراو تاکو کڕیارە عەرەبەکان دەستبەجێ متمانەیان پێ بکەن.",
      useCase3Tags: ["دەنگی ڕەسەنی خۆت", "متمانەی دەستبەجێ"],
      faqTitle: "پرسیارە دووبارەبووەکان",
      faqs: [
        {
          q: "دۆبلاژکردنی ڤیدیۆیەک چەند کاتی دەوێت؟",
          a: "پرۆسێسکردنی ڤیدیۆ نزیکەی یەک لەسەر سێی کاتی ڤیدیۆکە دەخایەنێت. ڤیدیۆیەکی ٣ خولەکی لە نزیکەی ٦٠ چرکەدا ئامادە دەبێت.",
        },
        {
          q: "ئایا نبرە و هەستی دەنگی سەرەکی کەسەکە دەپارێزرێت؟",
          a: "بەڵێ، تەکنەلۆژیای زیرەکی دەستکرد هەمان تۆن، هەست و تایبەتمەندی دەنگی قسەکەرەکە کۆپی دەکات بە عەرەبی عێراقی ڕەوان.",
        },
        {
          q: "چی بەسەر میوزیك و دەنگەکانی باکگراوندی ڤیدیۆکە دێت؟",
          a: "سیستەم دەنگی قسە لە میوزیك جیا دەکاتەوە، تەنها قسەکان دۆبلاژ دەکات و میوزیكە ڕەسەنەکە بە تەواوی وەک خۆی تێکەڵ دەکاتەوە.",
        },
        {
          q: "ئایا پشتگیری چەند قسەکەرێک لە یەک ڤیدیۆدا دەکات؟",
          a: "بەڵێ، سیستەم دەنگی کەسە جیاوازەکان دەناسێتەوە و دەنگی جیاوازی دۆبلاژ بۆ هەر قسەکەرێک دادەنێت.",
        },
        {
          q: "چ جۆرە فۆرمات و کواڵێتییەک پەسەندە؟",
          a: "هەموو فۆرماتەکانی MP4, MOV, MKV, WEBM تاکو کواڵێتی 4K بە 60fps پەسەند دەکرێن.",
        },
      ],
      pricingTitle: "نرخی ئاشکرا و گونجاو",
      pricingSubtitle: "پلانێک هەڵبژێره کە گونجاوە لەگەڵ پێویستی بەرهەمهێنانت.",
      starterTitle: "سەرەتایی (Starter)",
      starterPrice: "$10",
      starterPeriod: "/مانگانە",
      starterRate: "٥ خولەک ($2.00/خولەک)",
      starterF1: "خێرایی ئاسایی GPU",
      starterF2: "کواڵێتی 1080p Full HD",
      starterF3: "پشتگیری ١ بۆ ٢ قسەکەر",
      starterF4: "مۆدێلی دەنگی ستاندارد",
      starterCta: "کڕینی ٥ خولەک",
      proTitle: "پڕۆ (Pro)",
      proPrice: "$20",
      proPeriod: "/مانگانە",
      proRate: "١٥ خولەک ($1.33/خولەک)",
      proBadge: "زۆرترین هەڵبژێردراو",
      proF1: "خێرایی ئەولەویەتی بەرز",
      proF2: "کواڵێتی 4K UHD",
      proF3: "دەنگی بەغدادی بە هەست و سۆزەوە",
      proF4: "پشتگیری تا ٥ قسەکەر لە ڤیدیۆدا",
      proF5: "پاراستنی بێ کەموکوڕی میوزیك",
      proCta: "کڕینی ١٥ خولەک",
      creatorTitle: "دروستکەر (Creator)",
      creatorPrice: "$99",
      creatorPeriod: "/مانگانە",
      creatorRate: "١٢٠ خولەک ($0.82/خولەک)",
      creatorF1: "بەرزترین ئەولەویەتی کاتی پرۆسێس",
      creatorF2: "کواڵێتی 4K 60fps ماستەر",
      creatorF3: "قسەکەری بێ سنوور لە ڤیدیۆدا",
      creatorF4: "دەستکاریکردنی تایبەتی مۆدێلی دەنگ",
      creatorF5: "دەستگەیشتن بە API و Webhooks",
      creatorCta: "کڕینی ١٢٠ خولەک",
      footerDesc: "پردی پەیوەندی لە نێوان شێوەزارەکان. دۆبلاژی زیرەکی دەستکرد بۆ میدیای کوردی و عەرەبی.",
      footerAddress1: "کۆمپانیای FIXDAI LLC (Doblaj)",
      footerAddress2: "3801 N Capital of Texas Hwy, Ste E240 #3958",
      footerAddress3: "Austin, TX 78746, Texas, USA",
      footerRights: "© 2026 FIXDAI LLC d/b/a Doblaj. هەموو مافەکان پارێزراون.",
      startTrial: "دەستپێکردن",
      dashboard: "داشبۆرد",
    },
    ar: {
      title: "لهجة عراقية متقنة. وبنفس نبرة صوتك.",
      subtitle:
        "استنساخ صوتك بدقة إلى عامية عراقية فصيحة لتكسب ثقة الزبائن فوراً وتزيد مبيعاتك—بدون أن تنطق كلمة واحدة.",
      ctaPrimary: "ارفع أول فيديو",
      ctaSecondary: "شاهد العرض التجريبي التفاعلي",
      statsAccuracy: "١٠٠٪",
      statsAccuracyLabel: "أصالة اللهجة البغدادية العراقية",
      statsSpeed: "١/٣ الوقت",
      statsSpeedLabel: "سرعة معالجة فائقة بالذكاء الاصطناعي",
      statsQuality: "4K UHD",
      statsQualityLabel: "تصدير فيديو عالي الدقة متزامن",
      statsStems: "مسارين صوت",
      statsStemsLabel: "عزل نقي للموسيقى والمؤثرات الأصلية",
      demoTitle: "استمع إلى الفرق مباشرة",
      originalBadge: "الصوت الأصلي (كردي سوراني)",
      dubbedBadge: "المدبلج بالذكاء الاصطناعي (عامية عراقية - بغداد)",
      speaker1Name: "أحمد ك.",
      kurdishTranscript: "ئەم مۆبایلە نوێیە یەکێکە لە باشترین بەرهەمەکانی ئەمساڵ، کامێراکەی زۆر بەهێزە و پاترییەکەی بە درێژایی ڕۆژ دەمێنێتەوە.",
      iraqiTranscript: "هذا التلفون الجديد واحد من أحسن الأجهزة هالسنة، كاميرته كلش قوية وبطاريته تكفيك طول اليوم بدون أي نقص.",
      featuresHeading: "المميزات",
      howItWorksHeading: "كيف يعمل",
      pricingHeading: "الأسعار",
      faqHeading: "الأسئلة الشائعة",
      howItWorksTitle: "من فيديو كردي إلى زبائن عرب في 3 خطوات",
      howItWorksSubtitle: "",
      step1Title: "١. ارفع الفيديو الكردي",
      step1Desc: "بدون أي إعدادات أو مونتاج معقد. فقط أرسل فيديو متجرك السوراني المعتاد من هاتفك—وسيتكفل نظامنا بالباقي تلقائياً.",
      step2Title: "٢. استنساخ صوتك تلقائياً",
      step2Desc: "يحافظ الذكاء الاصطناعي على نبرة صوتك وأصوات متجرك، مترجماً حديثك إلى عامية عراقية طبيعية لتكسب ثقة السياح فوراً.",
      step3Title: "٣. انشر واجذب الزبائن",
      step3Desc: "حمّل الفيديو الجاهز للنشر خلال ثوانٍ، وانشره مباشرة على تيك توك أو إنستغرام وابدأ بجذب المشترين العرب القادمين لمتجرك.",
      useCasesTitle: "لمن صُمم دۆبلاژ الذكي؟",
      useCasesSubtitle: "",
      useCase1Title: "تجار التجزئة ذوو البضائع الراكدة",
      useCase1Desc: "مخصص لأصحاب المتاجر الذين تجمدت أموالهم في بضائع غير مباعة ويحتاجون إلى سيولة نقدية فورية من السياح العرب ذوي الإنفاق العالي المارين أمام محلاتهم.",
      useCase1Tags: ["سيولة نقدية فورية", "جذب السياح المارين"],
      useCase2Title: "صناع الفيديو غير المرئيين",
      useCase2Desc: "مخصص للتجار الذين يبذلون جهداً في السوشيال ميديا لكن فيديوهاتهم لا تصل للجمهور العربي لأن الخوارزميات تتجاهل الفيديوهات ذات الترجمة النصية.",
      useCase2Tags: ["انتشار الفيديو عربياً", "تفضيل الخوارزميات"],
      useCase3Title: "التجار الباحثون عن الموثوقية والأصالة",
      useCase3Desc: "مخصص لأصحاب الأعمال الذين يرغبون باستنساخ أصواتهم إلى عامية عراقية فصيحة ليكسبوا ثقة المشترين العرب فوراً دون أي حاجز لغوي.",
      useCase3Tags: ["نبرة صوتك الحقيقية", "كسب الثقة الفورية"],
      faqTitle: "الأسئلة الأكثر تكراراً",
      faqs: [
        {
          q: "كم تستغرق عملية دبلجة الفيديو؟",
          a: "تستغرق المعالجة حوالي ثلث مدة الفيديو الحقيقية. فالفيديو ذو الثلاث دقائق ينتهي تجهيزه بالكامل خلال 60 ثانية تقريباً.",
        },
        {
          q: "هل تحافظ الأداة على نبرة ومشاعر صوت المتحدث الأصلي؟",
          a: "نعم، تقنية استنساخ الصوت بالذكاء الاصطناعي تحافظ على نبرة الصوت والمشاعر والشخصية الأصلية مع نطق عراقي طبيعي.",
        },
        {
          q: "ماذا يحدث للموسيقى والمؤثرات الصوتية الخلفية؟",
          a: "يقوم نظام عزل المسارات بفصل صوت الحديث عن الموسيقى، ويدبلج الحوار فقط ثم يعيد دمج الموسيقى الأصلية بلا أدنى خسارة بالجودة.",
        },
        {
          q: "هل تدعم الأداة مقاطع الفيديو التي تحتوي على أكثر من متحدث؟",
          a: "نعم، يتعرف النظام تلقائياً على المتحدثين المتعددين ويعين أصوات ذكاء اصطناعي منفصلة ومطابقة لكل شخص بالفيديو.",
        },
        {
          q: "ما هي صيغ ودقة الفيديو المدعومة؟",
          a: "ندعم جميع الصيغ الشائعة MP4, MOV, MKV, WEBM وحتى دقة 4K بمعدل 60 إطاراً في الثانية.",
        },
      ],
      pricingTitle: "أسعار واضحة ومرنة",
      pricingSubtitle: "اختر الخطة المناسبة لحجم إنتاجك الصوتي والمرئي بدون أي رسوم خفية.",
      starterTitle: "المبتدئ (Starter)",
      starterPrice: "$10",
      starterPeriod: "/شهرياً",
      starterRate: "٥ دقائق ($2.00/دقيقة)",
      starterF1: "سرعة معالجة قياسية",
      starterF2: "تصدير فيديو بدقة 1080p Full HD",
      starterF3: "دعم متحدث أو متحدثين",
      starterF4: "نموذج استنساخ صوت قياسي",
      starterCta: "شراء ٥ دقائق",
      proTitle: "المحترف (Pro)",
      proPrice: "$20",
      proPeriod: "/شهرياً",
      proRate: "١٥ دقيقة ($1.33/دقيقة)",
      proBadge: "الأكثر طلباً",
      proF1: "أولوية معالجة سريعة جداً",
      proF2: "تصدير فيديو بدقة 4K UHD",
      proF3: "أصوات بغدادية معبرة بمشاعر عالية",
      proF4: "دعم حتى ٥ متحدثين في الفيديو",
      proF5: "إعادة دمج موسيقى بدون فقدان",
      proCta: "شراء ١٥ دقيقة",
      creatorTitle: "الشركات (Creator)",
      creatorPrice: "$99",
      creatorPeriod: "/شهرياً",
      creatorRate: "١٢٠ دقيقة ($0.82/دقيقة)",
      creatorF1: "أعلى أولوية طابور فوري",
      creatorF2: "تصدير بدقة 4K 60fps بدون ضغط",
      creatorF3: "عدد متحدثين غير محدود",
      creatorF4: "ضبط وتدريب مخصص للأصوات",
      creatorF5: "وصول كامل للـ API و Webhooks",
      creatorCta: "شراء ١٢٠ دقيقة",
      footerDesc: "جسر التواصل بين اللهجات. دبلجة الفيديو الذكية لربط الإعلام الكردي بالسوق العراقي.",
      footerAddress1: "شركة FIXDAI LLC (دۆبلاژ)",
      footerAddress2: "3801 N Capital of Texas Hwy, Ste E240 #3958",
      footerAddress3: "Austin, TX 78746, Texas, USA",
      footerRights: "© 2026 FIXDAI LLC d/b/a Doblaj. جميع الحقوق محفوظة.",
      startTrial: "ابدأ",
      dashboard: "لوحة التحكم",
    },
  };

export default function SoraniqLandingPage() {
  const { isSignedIn } = useAuth();
  const [demoTrack, setDemoTrack] = useState<"kurdish" | "iraqi">("iraqi");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [lang, setLang] = useState<"en" | "ckb" | "ar">("en");
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeStep, setActiveStep] = useState(1);

  const isDark = theme === "dark";
  const isMobile = useIsMobile();

  const howItWorksRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: howItWorksRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (isMobile) return;
    if (latest < 0.35) {
      setActiveStep(1);
    } else if (latest < 0.68) {
      setActiveStep(2);
    } else {
      setActiveStep(3);
    }
  });

  const isRTL = lang === "ckb" || lang === "ar";
  const t = TRANSLATIONS[lang];

  return (
    <div
      dir={isRTL ? "rtl" : "ltr"}
      className={`min-h-screen ${
        isDark ? "bg-[#0c0a09] text-[#d6d3d1]" : "bg-[#f5f5f5] text-[#4e4e4e]"
      } ${
        isRTL ? "font-arabic" : "font-sans"
      } antialiased selection:bg-[#c8b8e0]/30 selection:text-[#0c0a09] relative transition-colors duration-300`}
    >
      {/* Signature Atmospheric Pastel Gradient Orbs - GPU Accelerated CSS */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Mint & Peach Bloom */}
        <div
          className={`absolute top-[-150px] left-1/2 -translate-x-1/2 w-[900px] h-[550px] animate-orb-1 ${
            isDark
              ? "bg-[radial-gradient(ellipse_at_top,_rgba(167,229,211,0.25),_rgba(244,197,168,0.18)_45%,_transparent_70%)] opacity-50"
              : "bg-[radial-gradient(ellipse_at_top,_rgba(167,229,211,0.5),_rgba(244,197,168,0.35)_45%,_transparent_70%)]"
          } pointer-events-none`}
        />
        {/* Lavender & Sky Bloom */}
        <div
          className={`absolute top-[650px] right-[-100px] w-[600px] h-[600px] animate-orb-2 ${
            isDark
              ? "bg-[radial-gradient(circle,_rgba(200,184,224,0.22),_rgba(168,200,232,0.15)_50%,_transparent_70%)] opacity-50"
              : "bg-[radial-gradient(circle,_rgba(200,184,224,0.4),_rgba(168,200,232,0.3)_50%,_transparent_70%)]"
          } pointer-events-none`}
        />
        {/* Rose Accent Bloom */}
        <div
          className={`absolute top-[1600px] left-[-120px] w-[650px] h-[650px] animate-orb-3 ${
            isDark
              ? "bg-[radial-gradient(circle,_rgba(232,184,196,0.2),_transparent_70%)] opacity-50"
              : "bg-[radial-gradient(circle,_rgba(232,184,196,0.35),_transparent_70%)]"
          } pointer-events-none`}
        />
      </div>

      {/* TopNavBar */}
      <nav
        className={`fixed top-0 w-full z-50 ${
          isDark ? "bg-[#0c0a09]/85 border-[#292524]" : "bg-[#f5f5f5]/85 border-[#e7e5e4]"
        } backdrop-blur-md border-b transition-all duration-300`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 flex justify-between items-center h-16">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-8 h-8 rounded-full overflow-hidden border border-[#e7e5e4] dark:border-[#292524] shadow-sm flex items-center justify-center bg-white dark:bg-[#141211] shrink-0">
              <img src="/images/logo.webp" alt="Doblaj" width="32" height="32" className="w-full h-full object-cover" />
            </div>
            <span
              className={`text-xl font-serif font-normal ${
                isDark ? "text-white" : "text-[#0c0a09]"
              } tracking-tight transition-colors`}
            >
              Doblaj
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-[15px] font-medium tracking-[0.16px]">
            <a
              href="#demo"
              className={`${
                isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
              } transition-colors`}
            >
              {t.ctaSecondary}
            </a>
            <a
              href="#how-it-works"
              className={`${
                isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
              } transition-colors`}
            >
              {t.howItWorksHeading}
            </a>
            <a
              href="#use-cases"
              className={`${
                isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
              } transition-colors`}
            >
              {t.featuresHeading}
            </a>
            <a
              href="#pricing"
              className={`${
                isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
              } transition-colors`}
            >
              {t.pricingHeading}
            </a>
            <a
              href="#faq"
              className={`${
                isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
              } transition-colors`}
            >
              {t.faqHeading}
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            {/* Theme Toggle Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={toggleTheme}
              aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
              className={`p-2 rounded-full border transition-all flex items-center justify-center ${
                isDark
                  ? "bg-[#1c1917] border-[#292524] text-amber-300 hover:bg-[#292524]"
                  : "bg-[#f0efed] border-[#e7e5e4] text-[#0c0a09] hover:bg-[#e7e5e4]"
              }`}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </motion.button>

            {/* Language Switcher */}
            <div
              className={`flex items-center ${
                isDark
                  ? "bg-[#1c1917] border-[#292524] text-[#a8a29e]"
                  : "bg-[#f0efed] border-[#e7e5e4] text-[#777169]"
              } border rounded-full p-1 text-xs`}
            >
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 rounded-full font-medium transition-all ${
                  lang === "en"
                    ? isDark
                      ? "bg-[#292524] text-white font-semibold shadow-sm"
                      : "bg-white text-[#0c0a09] font-semibold shadow-sm"
                    : isDark
                    ? "hover:text-white"
                    : "hover:text-[#0c0a09]"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("ckb")}
                className={`px-3 py-1 rounded-full font-medium transition-all ${
                  lang === "ckb"
                    ? isDark
                      ? "bg-[#292524] text-white font-semibold shadow-sm"
                      : "bg-white text-[#0c0a09] font-semibold shadow-sm"
                    : isDark
                    ? "hover:text-white"
                    : "hover:text-[#0c0a09]"
                }`}
              >
                کوردی
              </button>
              <button
                onClick={() => setLang("ar")}
                className={`px-3 py-1 rounded-full font-medium transition-all ${
                  lang === "ar"
                    ? isDark
                      ? "bg-[#292524] text-white font-semibold shadow-sm"
                      : "bg-white text-[#0c0a09] font-semibold shadow-sm"
                    : isDark
                    ? "hover:text-white"
                    : "hover:text-[#0c0a09]"
                }`}
              >
                عربي
              </button>
            </div>

            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Link
                to={isSignedIn ? "/dubbing" : `/sign-up?redirect_url=${encodeURIComponent("/dubbing")}`}
                className={`inline-flex items-center justify-center px-5 py-2 rounded-full ${
                  isDark
                    ? "bg-white text-[#0c0a09] hover:bg-[#f0efed]"
                    : "bg-[#292524] text-white hover:bg-[#0c0a09]"
                } text-[15px] font-medium transition-all shadow-sm`}
              >
                {isSignedIn ? t.dashboard : t.startTrial}
              </Link>
            </motion.div>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
              className={`p-2 rounded-full border transition-all flex items-center justify-center ${
                isDark
                  ? "bg-[#1c1917] border-[#292524] text-amber-300"
                  : "bg-[#f0efed] border-[#e7e5e4] text-[#0c0a09]"
              }`}
            >
              {isDark ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                  />
                </svg>
              )}
            </button>

            <button
              onClick={() => setMobileMenu(!mobileMenu)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenu}
              className={`p-2 ${isDark ? "text-white" : "text-[#0c0a09]"}`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {mobileMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`md:hidden ${
              isDark ? "bg-[#0c0a09] border-[#292524]" : "bg-[#f5f5f5] border-[#e7e5e4]"
            } border-b px-6 py-6 space-y-4`}
          >
            <div className="flex items-center gap-2 mb-4">
              <button
                onClick={() => { setLang("en"); setMobileMenu(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  lang === "en"
                    ? isDark
                      ? "bg-white text-[#0c0a09]"
                      : "bg-[#292524] text-white"
                    : isDark
                    ? "bg-[#1c1917] text-[#d6d3d1] border border-[#292524]"
                    : "bg-white text-[#4e4e4e] border border-[#e7e5e4]"
                }`}
              >
                English
              </button>
              <button
                onClick={() => { setLang("ckb"); setMobileMenu(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  lang === "ckb"
                    ? isDark
                      ? "bg-white text-[#0c0a09]"
                      : "bg-[#292524] text-white"
                    : isDark
                    ? "bg-[#1c1917] text-[#d6d3d1] border border-[#292524]"
                    : "bg-white text-[#4e4e4e] border border-[#e7e5e4]"
                }`}
              >
                کوردی
              </button>
              <button
                onClick={() => { setLang("ar"); setMobileMenu(false); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                  lang === "ar"
                    ? isDark
                      ? "bg-white text-[#0c0a09]"
                      : "bg-[#292524] text-white"
                    : isDark
                    ? "bg-[#1c1917] text-[#d6d3d1] border border-[#292524]"
                    : "bg-white text-[#4e4e4e] border border-[#e7e5e4]"
                }`}
              >
                عربي
              </button>
            </div>
            <a
              href="#demo"
              onClick={() => setMobileMenu(false)}
              className={`block text-base font-medium ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"}`}
            >
              {t.ctaSecondary}
            </a>
            <a
              href="#how-it-works"
              onClick={() => setMobileMenu(false)}
              className={`block text-base font-medium ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"}`}
            >
              {t.howItWorksHeading}
            </a>
            <a
              href="#use-cases"
              onClick={() => setMobileMenu(false)}
              className={`block text-base font-medium ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"}`}
            >
              {t.featuresHeading}
            </a>
            <a
              href="#pricing"
              onClick={() => setMobileMenu(false)}
              className={`block text-base font-medium ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"}`}
            >
              {t.pricingHeading}
            </a>
            <a
              href="#faq"
              onClick={() => setMobileMenu(false)}
              className={`block text-base font-medium ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"}`}
            >
              {t.faqHeading}
            </a>
            <Link
              to={isSignedIn ? "/dubbing" : `/sign-up?redirect_url=${encodeURIComponent("/dubbing")}`}
              className={`block w-full text-center px-6 py-3 rounded-full ${
                isDark ? "bg-white text-[#0c0a09]" : "bg-[#292524] text-white"
              } font-medium text-sm hover:opacity-90`}
            >
              {isSignedIn ? t.dashboard : t.startTrial}
            </Link>
          </motion.div>
        )}
      </nav>

      {/* Hero Section - Full Screen Viewport */}
      <main className="pt-20 pb-12">
        <div className="relative overflow-hidden w-full min-h-[calc(100vh-5rem)] flex flex-col justify-center">
          {/* Ambient looping curved speech stream into ribbon animation (hidden on mobile for instant loading & 60fps) */}
          <div className="hidden md:block absolute inset-0 pointer-events-none z-0 overflow-hidden select-none">
            <SpeechStreamAnimation isDark={isDark} />
          </div>

          <section className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 flex-1 flex flex-col items-center justify-center text-center">
            {/* Hero Text Column - Centered (Non-blocking for instant LCP) */}
            <div className="space-y-8 max-w-3xl flex flex-col items-center">
              <h1
                className={`text-4xl sm:text-6xl lg:text-[72px] font-serif font-light ${
                  isDark ? "text-white" : "text-[#0c0a09]"
                } tracking-[-1.92px] leading-[1.04]`}
              >
                {t.title}
              </h1>

              <p
                className={`text-lg sm:text-xl lg:text-[22px] max-w-2xl ${
                  isDark ? "text-[#a8a29e]" : "text-[#777169]"
                } leading-relaxed font-sans tracking-[0.16px]`}
              >
                {t.subtitle}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center items-center">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Link
                    to={isSignedIn ? "/dubbing" : `/sign-up?redirect_url=${encodeURIComponent("/dubbing")}`}
                    className={`inline-flex items-center justify-center px-8 py-3.5 rounded-full ${
                      isDark
                        ? "bg-white text-[#0c0a09] hover:bg-[#f0efed]"
                        : "bg-[#292524] text-white hover:bg-[#0c0a09]"
                    } font-medium text-[15px] transition-all shadow-sm w-full sm:w-auto`}
                  >
                    {t.ctaPrimary}
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <a
                    href="#demo"
                    className={`inline-flex items-center justify-center px-8 py-3.5 rounded-full bg-transparent border ${
                      isDark
                        ? "border-[#292524] text-white hover:bg-white/5"
                        : "border-[#d6d3d1] text-[#0c0a09] hover:bg-black/5"
                    } font-medium text-[15px] transition-all w-full sm:w-auto`}
                  >
                    {t.ctaSecondary}
                  </a>
                </motion.div>
              </div>
            </div>
          </section>
        </div>

      {/* Trust Metrics / Spec Banner with Infinite Sliding Marquee from Right to Left */}
      <section
        className={`border-y ${
          isDark ? "border-[#292524] bg-[#141211]" : "border-[#e7e5e4] bg-[#fafafa]"
        } mt-16 lg:mt-24 py-6 overflow-hidden relative`}
      >
          {/* Edge gradient masks for smooth fade */}
          <div
            className={`absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r ${
              isDark ? "from-[#141211] to-transparent" : "from-[#fafafa] to-transparent"
            } z-10 pointer-events-none`}
          />
          <div
            className={`absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l ${
              isDark ? "from-[#141211] to-transparent" : "from-[#fafafa] to-transparent"
            } z-10 pointer-events-none`}
          />

          <div className="flex overflow-hidden" dir="ltr">
            <div className="animate-marquee items-center gap-12 sm:gap-20 shrink-0 pr-12 sm:pr-20">
              {[
                { value: t.statsAccuracy, label: t.statsAccuracyLabel },
                { value: t.statsSpeed, label: t.statsSpeedLabel },
                { value: t.statsQuality, label: t.statsQualityLabel },
                { value: t.statsStems, label: t.statsStemsLabel },
                { value: t.statsAccuracy, label: t.statsAccuracyLabel },
                { value: t.statsSpeed, label: t.statsSpeedLabel },
                { value: t.statsQuality, label: t.statsQualityLabel },
                { value: t.statsStems, label: t.statsStemsLabel },
              ].map((stat, i) => (
                <div key={i} className="flex items-center gap-12 sm:gap-20 shrink-0">
                  <div className="flex flex-col items-center text-center shrink-0">
                    <div
                      className={`text-2xl sm:text-4xl font-serif font-light tracking-tight ${
                        isDark ? "text-white" : "text-[#0c0a09]"
                      }`}
                    >
                      {stat.value}
                    </div>
                    <div
                      className={`text-xs sm:text-sm ${
                        isDark ? "text-[#a8a29e]" : "text-[#777169]"
                      } font-medium whitespace-nowrap mt-1 tracking-wide uppercase font-sans`}
                    >
                      {stat.label}
                    </div>
                  </div>
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      isDark ? "bg-[#292524]" : "bg-[#d6d3d1]"
                    } shrink-0`}
                  ></span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Interactive Before & After Voice Demo Section with Oscillating Waveforms */}
        <section id="demo" className="py-24 px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2
              className={`text-3xl sm:text-4xl font-serif font-light ${
                isDark ? "text-white" : "text-[#0c0a09]"
              } tracking-[-0.36px]`}
            >
              {t.demoTitle}
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className={`max-w-4xl mx-auto ${
              isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
            } border rounded-2xl p-6 sm:p-10 shadow-[0_4px_24px_rgba(0,0,0,0.04)] relative overflow-hidden`}
          >
            <div
              className={`flex flex-col sm:flex-row items-center justify-between gap-4 pb-8 border-b ${
                isDark ? "border-[#292524]" : "border-[#e7e5e4]"
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-full ${
                    isDark
                      ? "bg-[#292524] text-white border-[#383330]"
                      : "bg-[#f0efed] text-[#0c0a09] border-[#e7e5e4]"
                  } border flex items-center justify-center shadow-sm`}
                >
                  <span className="text-xs font-serif font-light">AK</span>
                </div>
                <div>
                  <h4 className={`font-medium text-base ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                    {t.speaker1Name}
                  </h4>
                </div>
              </div>

              {/* Track Toggle Buttons */}
              <div
                className={`flex items-center ${
                  isDark ? "bg-[#141211] border-[#292524]" : "bg-[#f0efed] border-[#e7e5e4]"
                } border rounded-full p-1 gap-1 w-full sm:w-auto`}
              >
                <button
                  onClick={() => setDemoTrack("kurdish")}
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                    demoTrack === "kurdish"
                      ? isDark
                        ? "bg-white text-[#0c0a09] shadow-sm"
                        : "bg-[#292524] text-white shadow-sm"
                      : isDark
                      ? "text-[#a8a29e] hover:text-white"
                      : "text-[#777169] hover:text-[#0c0a09]"
                  }`}
                >
                  {t.originalBadge}
                </button>
                <button
                  onClick={() => setDemoTrack("iraqi")}
                  className={`flex-1 sm:flex-none px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                    demoTrack === "iraqi"
                      ? isDark
                        ? "bg-white text-[#0c0a09] shadow-sm"
                        : "bg-[#292524] text-white shadow-sm"
                      : isDark
                      ? "text-[#a8a29e] hover:text-white"
                      : "text-[#777169] hover:text-[#0c0a09]"
                  }`}
                >
                  ✨ {t.dubbedBadge}
                </button>
              </div>
            </div>

            {/* Transcript & Waveform Visualization with AnimatePresence */}
            <div className="py-8 space-y-6">
              <div
                className={`p-6 rounded-xl ${
                  isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
                } border min-h-[120px] flex flex-col justify-center`}
              >
                <div
                  className={`text-xs font-mono ${
                    isDark ? "text-[#a8a29e]" : "text-[#777169]"
                  } mb-2 uppercase tracking-wider`}
                >
                  {demoTrack === "kurdish" ? "Original Kurdish Speech" : "Synchronized Iraqi Arabic Dub"}
                </div>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={demoTrack}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className={`text-lg sm:text-xl ${
                      isDark ? "text-white" : "text-[#0c0a09]"
                    } font-serif font-light leading-relaxed`}
                  >
                    "{demoTrack === "kurdish" ? t.kurdishTranscript : t.iraqiTranscript}"
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Dynamic Audio Waveform Bars */}
              <div
                className={`flex items-center justify-between gap-1 sm:gap-1.5 h-16 px-4 ${
                  isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
                } rounded-xl border overflow-hidden`}
              >
                {[40, 65, 30, 85, 95, 45, 70, 60, 90, 100, 55, 75, 40, 80, 95, 60, 45, 85, 90, 70, 50, 65, 80, 40].map(
                  (baseHeight, i) => (
                    <div
                      key={i}
                      className="w-full max-w-[8px] rounded-full wave-bar"
                      style={{
                        height: `${baseHeight}%`,
                        backgroundColor: isDark ? "#ffffff" : "#292524",
                        opacity: 0.4 + ((i * 13) % 50) / 100,
                        animationDelay: `${(i % 8) * 0.12}s`,
                        animationDuration: `${0.9 + (i % 5) * 0.15}s`,
                      }}
                    />
                  )
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end gap-4 pt-4 text-xs">
              <Link
                to={isSignedIn ? "/dubbing" : `/sign-up?redirect_url=${encodeURIComponent("/dubbing")}`}
                className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-medium hover:underline`}
              >
                Dub your own video now →
              </Link>
            </div>
          </motion.div>
        </section>

        {/* How it Works - Responsive Scroll Experience (280vh sticky track on desktop, smooth natural flow on mobile) */}
        <section
          ref={howItWorksRef}
          className={`relative h-auto py-16 lg:py-0 lg:h-[280vh] ${isDark ? "bg-[#0c0a09]" : "bg-[#f5f5f5]"}`}
          id="how-it-works"
        >
          <div className="relative lg:sticky lg:top-0 lg:h-screen w-full flex flex-col justify-center px-4 sm:px-6 lg:px-10 max-w-7xl mx-auto overflow-hidden">
            {/* Top Bar: Section Title & Step Indicator */}
            <div className={`mb-6 sm:mb-8 border-b ${isDark ? "border-[#292524]" : "border-[#e7e5e4]"} pb-4 sm:pb-6`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <div
                    className={`inline-flex items-center gap-2 px-3.5 py-1 rounded-full ${
                      isDark ? "bg-[#1c1917] text-white border-[#292524]" : "bg-[#f0efed] text-[#0c0a09] border-[#e7e5e4]"
                    } text-xs font-mono mb-2 border`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#16a34a] animate-pulse"></span>
                    HOW IT WORKS • STEP 0{activeStep} / 03
                  </div>
                  <h2
                    className={`text-2xl sm:text-4xl font-serif font-light ${
                      isDark ? "text-white" : "text-[#0c0a09]"
                    } tracking-[-0.36px]`}
                  >
                    {t.howItWorksTitle}
                  </h2>
                </div>

                {/* Mobile Step Selector (Instant touch tabs on mobile/tablet) */}
                <div className="flex items-center gap-2 lg:hidden">
                  {[1, 2, 3].map((stepNum) => (
                    <button
                      key={stepNum}
                      type="button"
                      onClick={() => setActiveStep(stepNum)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-mono font-medium transition-all ${
                        activeStep === stepNum
                          ? isDark
                            ? "bg-white text-[#0c0a09] shadow-sm font-semibold"
                            : "bg-[#292524] text-white shadow-sm font-semibold"
                          : isDark
                          ? "bg-[#141211] text-[#a8a29e] border border-[#292524]"
                          : "bg-white text-[#777169] border border-[#e7e5e4]"
                      }`}
                    >
                      {stepNum === 1 ? "01. Upload" : stepNum === 2 ? "02. AI Dub" : "03. Attract"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Stage Grid: Left = Active Step Panel, Right = Synchronized Animated Stage */}
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-center">
              {/* Left Column: Active Step Card with Upward Slide & Fade */}
              <div className="lg:col-span-5 relative min-h-[220px] sm:min-h-[260px] flex flex-col justify-center">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`panel-step-${activeStep}`}
                    initial={{ opacity: 0, y: 35 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -35 }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                    className={`space-y-4 ${
                      isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                    } border rounded-3xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] relative overflow-hidden`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-9 h-9 rounded-full ${
                          isDark ? "bg-white text-[#0c0a09]" : "bg-[#292524] text-white"
                        } font-serif font-light text-base flex items-center justify-center shadow-sm`}
                      >
                        {activeStep}
                      </span>
                      <span
                        className={`text-xs font-mono uppercase tracking-wider ${
                          isDark ? "text-[#a8a29e]" : "text-[#777169]"
                        } font-medium`}
                      >
                        {activeStep === 1
                          ? "Step 1: Upload"
                          : activeStep === 2
                          ? "Step 2: AI Voice Clone"
                          : "Step 3: Post & Capture"}
                      </span>
                    </div>

                    <h3
                      className={`text-xl sm:text-2xl font-serif font-normal ${
                        isDark ? "text-white" : "text-[#0c0a09]"
                      } leading-snug`}
                    >
                      {activeStep === 1
                        ? t.step1Title
                        : activeStep === 2
                        ? t.step2Title
                        : t.step3Title}
                    </h3>

                    <p className={`text-sm sm:text-base ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"} leading-relaxed`}>
                      {activeStep === 1
                        ? t.step1Desc
                        : activeStep === 2
                        ? t.step2Desc
                        : t.step3Desc}
                    </p>

                    <div
                      className={`pt-2 hidden lg:flex items-center gap-2 text-xs font-mono ${
                        isDark ? "text-[#a8a29e]" : "text-[#777169]"
                      }`}
                    >
                      <span>↓</span>
                      <span>Scroll down to advance steps</span>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Right Column: Synchronized Animated Illustration Stage */}
              <div className="lg:col-span-7">
                <div
                  className={`${
                    isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                  } border rounded-3xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] relative min-h-[420px] flex flex-col justify-center overflow-hidden`}
                >
                  {/* Subtle Pastel Radial Glows */}
                  <div className="absolute -top-20 -right-20 w-64 h-64 bg-[radial-gradient(circle,_rgba(167,229,211,0.25),_transparent_70%)] pointer-events-none" />
                  <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[radial-gradient(circle,_rgba(200,184,224,0.25),_transparent_70%)] pointer-events-none" />

                  <AnimatePresence mode="wait">
                    {activeStep === 1 && (
                      <motion.div
                        key="step-1-animation"
                        initial={{ opacity: 0, y: 35, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -35, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="space-y-6"
                      >
                        {/* Header Badge */}
                        <div
                          className={`flex items-center justify-between border-b ${
                            isDark ? "border-[#292524]" : "border-[#e7e5e4]"
                          } pb-4`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#16a34a] animate-ping" />
                            <span
                              className={`text-xs font-mono ${
                                isDark ? "text-white" : "text-[#0c0a09]"
                              } font-medium`}
                            >
                              STEP 1: RAW VIDEO INGESTION
                            </span>
                          </div>
                          <span
                            className={`text-xs ${
                              isDark ? "text-[#a8a29e] bg-[#141211] border-[#292524]" : "text-[#777169] bg-[#f0efed] border-[#e7e5e4]"
                            } font-mono px-2.5 py-1 rounded-full border`}
                          >
                            Auto Phone Ingest
                          </span>
                        </div>

                        {/* Video Dropzone Mockup */}
                        <div
                          className={`p-6 rounded-2xl ${
                            isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#d6d3d1]"
                          } border-2 border-dashed flex flex-col items-center justify-center text-center space-y-4 relative overflow-hidden`}
                        >
                          <motion.div
                            animate={isMobile ? undefined : { scale: [1, 1.05, 1] }}
                            transition={isMobile ? undefined : { repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                            className={`w-16 h-16 rounded-2xl ${
                              isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                            } border flex items-center justify-center text-3xl shadow-sm`}
                          >
                            📱
                          </motion.div>
                          <div className="space-y-1">
                            <h4 className={`font-medium text-sm sm:text-base ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                              Erbil_Store_Tour_Kurdish.mp4
                            </h4>
                            <p className={`text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"}`}>
                              4K UHD 60fps • 48.2 MB • Recorded from iPhone / Android
                            </p>
                          </div>

                          {/* Upload Progress Bar Simulation */}
                          <div
                            className={`w-full max-w-xs ${
                              isDark ? "bg-[#292524] border-[#383330]" : "bg-[#f0efed] border-[#e7e5e4]"
                            } rounded-full h-2 overflow-hidden border`}
                          >
                            <motion.div
                              initial={{ width: "15%" }}
                              animate={{ width: "100%" }}
                              transition={{ duration: 1.8, ease: "easeOut" }}
                              className={`h-full ${isDark ? "bg-white" : "bg-[#292524]"} rounded-full`}
                            />
                          </div>
                        </div>

                        {/* Vocal Stem Extraction Status Badges */}
                        <div className="grid grid-cols-2 gap-3 pt-2 text-xs font-mono">
                          <div
                            className={`p-3 rounded-xl ${
                              isDark ? "bg-[#141211] border-[#292524] text-white" : "bg-[#fafafa] border-[#e7e5e4] text-[#0c0a09]"
                            } border flex items-center gap-2`}
                          >
                            <span className="w-2 h-2 rounded-full bg-[#16a34a]" />
                            <span>🎙️ Speech Isolated</span>
                          </div>
                          <div
                            className={`p-3 rounded-xl ${
                              isDark ? "bg-[#141211] border-[#292524] text-white" : "bg-[#fafafa] border-[#e7e5e4] text-[#0c0a09]"
                            } border flex items-center gap-2`}
                          >
                            <span className={`w-2 h-2 rounded-full ${isDark ? "bg-white" : "bg-[#292524]"}`} />
                            <span>🎵 Ambience Preserved</span>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {activeStep === 2 && (
                      <motion.div
                        key="step-2-animation"
                        initial={{ opacity: 0, y: 35, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -35, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="space-y-6"
                      >
                        {/* Header Badge */}
                        <div
                          className={`flex items-center justify-between border-b ${
                            isDark ? "border-[#292524]" : "border-[#e7e5e4]"
                          } pb-4`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${isDark ? "bg-white" : "bg-[#292524]"} animate-ping`} />
                            <span
                              className={`text-xs font-mono ${
                                isDark ? "text-white" : "text-[#0c0a09]"
                              } font-medium`}
                            >
                              STEP 2: VOICE CLONING & DIALECT
                            </span>
                          </div>
                          <span
                            className={`text-xs ${
                              isDark ? "text-white bg-[#141211] border-[#292524]" : "text-[#0c0a09] bg-[#f0efed] border-[#e7e5e4]"
                            } font-mono px-2.5 py-1 rounded-full border`}
                          >
                            99.8% Timbre Match
                          </span>
                        </div>

                        {/* Dialect Translation Card */}
                        <div className="space-y-3">
                          <div
                            className={`p-4 rounded-xl ${
                              isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
                            } border flex items-center justify-between`}
                          >
                            <div className="space-y-0.5">
                              <span
                                className={`text-[11px] font-mono ${
                                  isDark ? "text-[#a8a29e]" : "text-[#777169]"
                                } uppercase`}
                              >
                                Kurdish Sorani Input
                              </span>
                              <p className={`text-sm font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                                "ئەم کەلوپەلانە هەمووی ڕەسەنن و بە گەرەنتی دەیاندەین"
                              </p>
                            </div>
                            <span className="text-lg">🗣️</span>
                          </div>

                          <div className="flex items-center justify-center">
                            <motion.div
                              animate={isMobile ? undefined : { y: [0, 3, 0] }}
                              transition={isMobile ? undefined : { repeat: Infinity, duration: 1.4 }}
                              className={`px-3.5 py-1 rounded-full ${
                                isDark ? "bg-[#141211] border-[#292524] text-white" : "bg-[#f0efed] border-[#e7e5e4] text-[#0c0a09]"
                              } border text-xs font-mono font-medium`}
                            >
                              ⚡ Baghdad Dialect AI Transfer
                            </motion.div>
                          </div>

                          <div
                            className={`p-4 rounded-xl ${
                              isDark ? "bg-[#292524] border-white/20" : "bg-white border-[#292524]/30"
                            } border flex items-center justify-between shadow-sm`}
                          >
                            <div className="space-y-0.5">
                              <span
                                className={`text-[11px] font-mono ${
                                  isDark ? "text-white" : "text-[#0c0a09]"
                                } uppercase font-semibold`}
                              >
                                Cloned Iraqi Dub Output
                              </span>
                              <p className={`text-sm font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                                "هاي البضاعة كلها أصلية ومضمونة، وتكدر تاخذها هسة"
                              </p>
                            </div>
                            <span className="text-lg">✨</span>
                          </div>
                        </div>

                        {/* Live Synthesizer Waveform Visual */}
                        <div
                          className={`flex items-center justify-between gap-1 h-12 px-4 ${
                            isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
                          } rounded-xl border overflow-hidden`}
                        >
                          {[35, 70, 45, 90, 60, 80, 100, 50, 85, 40, 95, 75, 55, 90, 65, 45, 80, 95, 60, 30].map(
                            (h, i) => (
                              <div
                                key={i}
                                className={`w-full max-w-[6px] ${isDark ? "bg-white" : "bg-[#292524]"} rounded-full opacity-60 wave-bar`}
                                style={{
                                  height: `${h}%`,
                                  animationDelay: `${(i % 5) * 0.15}s`,
                                  animationDuration: `${0.8 + (i % 4) * 0.2}s`,
                                }}
                              />
                            )
                          )}
                        </div>
                      </motion.div>
                    )}

                    {activeStep === 3 && (
                      <motion.div
                        key="step-3-animation"
                        initial={{ opacity: 0, y: 35, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -35, scale: 0.96 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="space-y-6"
                      >
                        {/* Header Badge */}
                        <div
                          className={`flex items-center justify-between border-b ${
                            isDark ? "border-[#292524]" : "border-[#e7e5e4]"
                          } pb-4`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#16a34a] animate-ping" />
                            <span
                              className={`text-xs font-mono ${
                                isDark ? "text-white" : "text-[#0c0a09]"
                              } font-medium`}
                            >
                              STEP 3: POST & ATTRACT TOURISTS
                            </span>
                          </div>
                          <span
                            className={`text-xs ${
                              isDark ? "text-white bg-[#141211] border-[#292524]" : "text-[#0c0a09] bg-[#f0efed] border-[#e7e5e4]"
                            } font-mono px-2.5 py-1 rounded-full border`}
                          >
                            Instant Viral Broadcast
                          </span>
                        </div>

                        {/* Social Video Card with Floating Inbound Tourist DMs */}
                        <div
                          className={`p-5 rounded-2xl ${
                            isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
                          } border space-y-4`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="w-2.5 h-2.5 rounded-full bg-[#dc2626] animate-pulse" />
                              <span className={`text-xs font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                                TikTok & Instagram Live
                              </span>
                            </div>
                            <span className="text-xs font-mono text-[#16a34a] font-medium">
                              38.4K Arab Tourist Views
                            </span>
                          </div>

                          {/* Floating Customer Inquiries */}
                          <div className="space-y-2.5">
                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.15 }}
                              className={`p-3 rounded-xl ${
                                isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                              } border flex items-start gap-2.5 shadow-sm`}
                            >
                              <div
                                className={`w-7 h-7 rounded-full ${
                                  isDark ? "bg-[#292524] text-white" : "bg-[#f0efed] text-[#0c0a09]"
                                } flex items-center justify-center text-xs shrink-0`}
                              >
                                💬
                              </div>
                              <div>
                                <div className={`text-xs font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                                  سائح من بغداد (أربيل)
                                </div>
                                <div className={`text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"}`}>
                                  "مساء الخير، محلكم وين بالقلعة بالضبط؟ جايين زيارة اليوم ونريد نشتري"
                                </div>
                              </div>
                            </motion.div>

                            <motion.div
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.3 }}
                              className={`p-3 rounded-xl ${
                                isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                              } border flex items-start gap-2.5 shadow-sm`}
                            >
                              <div
                                className={`w-7 h-7 rounded-full ${
                                  isDark ? "bg-[#292524] text-white" : "bg-[#f0efed] text-[#0c0a09]"
                                } flex items-center justify-center text-xs shrink-0`}
                              >
                                📦
                              </div>
                              <div>
                                <div className={`text-xs font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                                  زبون من البصرة
                                </div>
                                <div className={`text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"}`}>
                                  "عاشت إيدكم على التوضيح، عدكم توصيل مباشر لبغداد والمحافظات؟"
                                </div>
                              </div>
                            </motion.div>
                          </div>
                        </div>

                        {/* Export Quick Action Buttons */}
                        <div className="flex items-center gap-3 pt-1">
                          <Link
                            to={isSignedIn ? "/dubbing" : `/sign-up?redirect_url=${encodeURIComponent("/dubbing")}`}
                            className={`flex-1 py-3 rounded-full ${
                              isDark ? "bg-white text-[#0c0a09] hover:bg-[#f0efed]" : "bg-[#292524] text-white hover:bg-[#0c0a09]"
                            } font-medium text-[15px] text-center transition-all shadow-sm`}
                          >
                            Upload & Dub Shop Video
                          </Link>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases Section with Hover Effects */}
        <section
          className={`py-24 px-4 sm:px-6 lg:px-10 ${
            isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
          } border-y`}
          id="use-cases"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-2xl mx-auto mb-16 space-y-4"
            >
              <h2
                className={`text-3xl sm:text-4xl font-serif font-light ${
                  isDark ? "text-white" : "text-[#0c0a09]"
                } tracking-[-0.36px]`}
              >
                {t.useCasesTitle}
              </h2>
              {t.useCasesSubtitle && (
                <p className={`text-base sm:text-lg ${isDark ? "text-[#a8a29e]" : "text-[#777169]"}`}>
                  {t.useCasesSubtitle}
                </p>
              )}
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
              {[
                { Graphic: AnimatedRetailerGraphic, title: t.useCase1Title, desc: t.useCase1Desc, tags: t.useCase1Tags },
                { Graphic: AnimatedCreatorGraphic, title: t.useCase2Title, desc: t.useCase2Desc, tags: t.useCase2Tags },
                { Graphic: AnimatedMerchantGraphic, title: t.useCase3Title, desc: t.useCase3Desc, tags: t.useCase3Tags },
              ].map((uc, idx) => {
                const GraphicComponent = uc.Graphic;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.15 }}
                    whileHover={{
                      y: -6,
                      boxShadow: isDark ? "0 12px 36px rgba(0, 0, 0, 0.4)" : "0 12px 36px rgba(0, 0, 0, 0.06)",
                    }}
                    className={`${
                      isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                    } rounded-3xl p-8 sm:p-10 border shadow-[0_4px_16px_rgba(0,0,0,0.03)] relative overflow-hidden group flex flex-col justify-between min-h-[480px] sm:min-h-[520px] lg:min-h-[560px] transition-all duration-300`}
                  >
                    {/* Upper Content */}
                    <div className="space-y-6">
                      {/* Index Pill & Animated Graphic */}
                      <div className="flex items-center justify-between">
                        <GraphicComponent isDark={isDark} />
                        <span
                          className={`text-xs font-mono font-medium ${
                            isDark
                              ? "text-white bg-[#141211] border-[#292524]"
                              : "text-[#0c0a09] bg-[#f0efed] border-[#e7e5e4]"
                          } px-3 py-1 rounded-full border`}
                        >
                          PROFILE 0{idx + 1}
                        </span>
                      </div>

                      {/* Title */}
                      <h3
                        className={`text-2xl sm:text-[26px] font-serif font-normal ${
                          isDark ? "text-white" : "text-[#0c0a09]"
                        } tracking-tight leading-snug`}
                      >
                        {uc.title}
                      </h3>

                      {/* Description */}
                      <p className={`${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"} text-sm sm:text-base leading-relaxed`}>
                        {uc.desc}
                      </p>
                    </div>

                    {/* Bottom Tags / Value Drivers */}
                    <div className={`pt-8 mt-6 border-t ${isDark ? "border-[#292524]" : "border-[#e7e5e4]"} space-y-2`}>
                      <div
                        className={`text-[10px] font-mono uppercase tracking-widest ${
                          isDark ? "text-[#a8a29e]" : "text-[#777169]"
                        } font-medium mb-2`}
                      >
                        CORE VALUE UNLOCKED
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {uc.tags?.map((tag: string, tIdx: number) => (
                          <span
                            key={tIdx}
                            className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full ${
                              isDark
                                ? "bg-[#141211] text-[#d6d3d1] border-[#292524]"
                                : "bg-[#f0efed] text-[#292524] border-[#e7e5e4]"
                            } border transition-all`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${isDark ? "bg-white" : "bg-[#292524]"}`}></span>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pricing Section with Inverted Editorial Dark Pro Card */}
        <section className={`py-24 px-4 sm:px-6 lg:px-10 ${isDark ? "bg-[#0c0a09]" : "bg-[#f5f5f5]"}`} id="pricing">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2
                className={`text-3xl sm:text-4xl font-serif font-light ${
                  isDark ? "text-white" : "text-[#0c0a09]"
                } mb-4 tracking-[-0.36px]`}
              >
                {t.pricingTitle}
              </h2>
              <p className={`text-base sm:text-lg ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} mb-12`}>
                {t.pricingSubtitle}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
              {/* Starter */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{ y: -4 }}
                className={`${
                  isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                } rounded-2xl p-8 border text-left flex flex-col justify-between shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:shadow-md transition-all`}
              >
                <div>
                  <h3 className={`text-xl font-serif font-normal ${isDark ? "text-white" : "text-[#0c0a09]"} mb-2`}>
                    {t.starterTitle}
                  </h3>
                  <div className={`text-4xl font-serif font-light ${isDark ? "text-white" : "text-[#0c0a09]"} mb-4`}>
                    {t.starterPrice}{" "}
                    <span className={`text-sm ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} font-sans`}>
                      {t.starterPeriod}
                    </span>
                  </div>
                  <p className={`text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} font-mono font-medium mb-6`}>
                    {t.starterRate}
                  </p>
                  <ul className={`text-sm ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"} space-y-3 mb-6`}>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.starterF1}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.starterF2}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.starterF3}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.starterF4}
                    </li>
                  </ul>
                </div>
                <Link
                  to={
                    isSignedIn
                      ? "/pricing?plan=starter"
                      : `/sign-up?redirect_url=${encodeURIComponent("/pricing?plan=starter")}`
                  }
                  className={`w-full py-3 rounded-full border ${
                    isDark
                      ? "border-[#292524] text-white hover:bg-white hover:text-[#0c0a09]"
                      : "border-[#d6d3d1] text-[#0c0a09] hover:bg-[#0c0a09] hover:text-white"
                  } text-center text-[15px] font-medium transition-all block`}
                >
                  {t.starterCta}
                </Link>
              </motion.div>

              {/* Pro - Inverted Dark Editorial Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                whileHover={{ y: -6 }}
                className={`${
                  isDark ? "bg-[#24201e] border-[#44403c]" : "bg-[#0c0a09] border-[#292524]"
                } text-white rounded-2xl p-8 border text-left flex flex-col justify-between relative shadow-2xl`}
              >
                <div className="absolute top-0 right-8 -translate-y-1/2 px-3.5 py-1 bg-white text-[#0c0a09] text-xs font-bold uppercase rounded-full tracking-wider shadow-md">
                  {t.proBadge}
                </div>
                <div>
                  <h3 className="text-xl font-serif font-normal text-white mb-2">{t.proTitle}</h3>
                  <div className="text-4xl font-serif font-light text-white mb-4">
                    {t.proPrice} <span className="text-sm text-[#a8a29e] font-sans">{t.proPeriod}</span>
                  </div>
                  <p className="text-xs text-[#d6d3d1] font-mono font-medium mb-6">{t.proRate}</p>
                  <ul className="text-sm text-[#d6d3d1] space-y-3 mb-6">
                    <li className="flex items-center gap-2">
                      <span className="text-white font-bold">✓</span> {t.proF1}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-white font-bold">✓</span> {t.proF2}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-white font-bold">✓</span> {t.proF3}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-white font-bold">✓</span> {t.proF4}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-white font-bold">✓</span> {t.proF5}
                    </li>
                  </ul>
                </div>
                <Link
                  to={
                    isSignedIn
                      ? "/pricing?plan=pro"
                      : `/sign-up?redirect_url=${encodeURIComponent("/pricing?plan=pro")}`
                  }
                  className="w-full py-3 rounded-full bg-white text-[#0c0a09] text-center text-[15px] font-medium hover:bg-[#f0efed] transition-all block shadow-sm"
                >
                  {t.proCta}
                </Link>
              </motion.div>

              {/* Creator */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                whileHover={{ y: -4 }}
                className={`${
                  isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                } rounded-2xl p-8 border text-left flex flex-col justify-between shadow-[0_4px_16px_rgba(0,0,0,0.02)] hover:shadow-md transition-all`}
              >
                <div>
                  <h3 className={`text-xl font-serif font-normal ${isDark ? "text-white" : "text-[#0c0a09]"} mb-2`}>
                    {t.creatorTitle}
                  </h3>
                  <div className={`text-4xl font-serif font-light ${isDark ? "text-white" : "text-[#0c0a09]"} mb-4`}>
                    {t.creatorPrice}{" "}
                    <span className={`text-sm ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} font-sans`}>
                      {t.creatorPeriod}
                    </span>
                  </div>
                  <p className={`text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} font-mono font-medium mb-6`}>
                    {t.creatorRate}
                  </p>
                  <ul className={`text-sm ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"} space-y-3 mb-6`}>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.creatorF1}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.creatorF2}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.creatorF3}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.creatorF4}
                    </li>
                    <li className="flex items-center gap-2">
                      <span className={`${isDark ? "text-white" : "text-[#0c0a09]"} font-bold`}>✓</span> {t.creatorF5}
                    </li>
                  </ul>
                </div>
                <Link
                  to={
                    isSignedIn
                      ? "/pricing?plan=creator"
                      : `/sign-up?redirect_url=${encodeURIComponent("/pricing?plan=creator")}`
                  }
                  className={`w-full py-3 rounded-full border ${
                    isDark
                      ? "border-[#292524] text-white hover:bg-white hover:text-[#0c0a09]"
                      : "border-[#d6d3d1] text-[#0c0a09] hover:bg-[#0c0a09] hover:text-white"
                  } text-center text-[15px] font-medium transition-all block`}
                >
                  {t.creatorCta}
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section - Clean Editorial Accordion */}
        <section
          className={`py-24 px-4 sm:px-6 lg:px-10 ${
            isDark ? "bg-[#141211] border-[#292524]" : "bg-[#fafafa] border-[#e7e5e4]"
          } border-t`}
          id="faq"
        >
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16 space-y-4"
            >
              <h2
                className={`text-3xl sm:text-4xl font-serif font-light ${
                  isDark ? "text-white" : "text-[#0c0a09]"
                } tracking-[-0.36px]`}
              >
                {t.faqTitle}
              </h2>
            </motion.div>

            <div className="space-y-3">
              {t.faqs.map((faq, idx) => {
                const isOpen = openFaq === idx;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.08 }}
                    className={`${
                      isDark ? "bg-[#1c1917] border-[#292524]" : "bg-white border-[#e7e5e4]"
                    } rounded-2xl border overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-colors`}
                  >
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                      className={`w-full p-6 text-left flex justify-between items-center gap-4 ${
                        isDark ? "hover:bg-[#24201e]" : "hover:bg-[#fafafa]"
                      } transition-colors`}
                      style={{ textAlign: isRTL ? "right" : "left" }}
                    >
                      <h4 className={`text-base sm:text-lg font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                        {faq.q}
                      </h4>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.25 }}
                        className={`${isDark ? "text-[#a8a29e]" : "text-[#777169]"} text-2xl font-light font-mono shrink-0`}
                      >
                        +
                      </motion.span>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div
                            className={`px-6 pb-6 pt-1 text-sm ${
                              isDark ? "text-[#d6d3d1] border-[#292524]" : "text-[#4e4e4e] border-[#f0efed]"
                            } leading-relaxed border-t`}
                          >
                            {faq.a}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer
        className={`${
          isDark ? "bg-[#0c0a09] border-[#292524] text-[#a8a29e]" : "bg-[#f5f5f5] border-[#e7e5e4] text-[#4e4e4e]"
        } border-t w-full py-16 px-4 sm:px-6 lg:px-10 transition-colors`}
        id="contact"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 space-y-4 pr-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-[#e7e5e4] dark:border-[#292524] shadow-sm flex items-center justify-center bg-white dark:bg-[#141211] shrink-0">
                <img src="/images/logo.webp" alt="Doblaj" width="32" height="32" loading="lazy" className="w-full h-full object-cover" />
              </div>
              <span className={`text-xl font-serif font-normal ${isDark ? "text-white" : "text-[#0c0a09]"}`}>
                Doblaj
              </span>
            </div>
            <p className={`text-sm ${isDark ? "text-[#d6d3d1]" : "text-[#4e4e4e]"} max-w-sm leading-relaxed`}>
              {t.footerDesc}
            </p>
            <div className={`pt-4 text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} space-y-1`}>
              <p className={`font-medium ${isDark ? "text-white" : "text-[#0c0a09]"}`}>{t.footerAddress1}</p>
              <p>{t.footerAddress2}</p>
              <p>{t.footerAddress3}</p>
            </div>
            <div className={`pt-2 text-xs ${isDark ? "text-[#a8a29e]" : "text-[#777169]"} space-y-1`}>
              <p>
                Support:{" "}
                <a
                  href="mailto:support@doblaj.com"
                  className={`${isDark ? "text-white" : "text-[#0c0a09]"} hover:underline`}
                >
                  support@doblaj.com
                </a>
              </p>
              <p>
                DMCA:{" "}
                <a
                  href="mailto:copyright@doblaj.com"
                  className={`${isDark ? "text-white" : "text-[#0c0a09]"} hover:underline`}
                >
                  copyright@doblaj.com
                </a>
              </p>
            </div>
            <p className={`text-xs ${isDark ? "text-[#777169]" : "text-[#a8a29e]"} pt-4`}>{t.footerRights}</p>
          </div>
          <div className="col-span-1 space-y-4">
            <h4 className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#0c0a09]"} uppercase tracking-wider`}>
              Product
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="#demo"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Interactive Demo
                </a>
              </li>
              <li>
                <a
                  href="#how-it-works"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Pipeline
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Pricing
                </a>
              </li>
              <li>
                <a
                  href="#faq"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Help & FAQ
                </a>
              </li>
            </ul>
          </div>
          <div className="col-span-1 space-y-4">
            <h4 className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#0c0a09]"} uppercase tracking-wider`}>
              Legal & Social
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link
                  to="/privacy"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/refund-policy"
                  className={`${
                    isDark ? "text-[#a8a29e] hover:text-white" : "text-[#4e4e4e] hover:text-[#0c0a09]"
                  } transition-colors`}
                >
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
